import * as React from "react";
import {
    ICustomerPortalService,
    Instalment,
    InstalmentsInfo,
    Party,
    PaymentCard,
    Policy,
    POLICY_STATUS_MTC,
    PREMIUM_TYPE_ANNUAL,
    PREMIUM_TYPE_MONTHLY,
    Renewal,
    RENEWAL_PROPOSAL_EDIT_VIEW_JOURNEY_URL,
    resourcesHelper,
    Types
} from "@falcon/services";
import {findMainAnnualPaymentCardByMainCardId, formatDateDMY, getAmountToDisplay} from "../../CommonFunctions";
import {StorageSuccessModal} from "./StorageSuccessModal";
import {Link} from "react-router-dom";
import {
    EditCarMtaSuccessModalWordingGenerator
} from "../../pages/dashboard/annual/motor/yourCarsPage/modal/EditCarMtaSuccessModalWordingGenerator";

import PaymentSchedule from "../../pages/dashboard/common/payments/paymentDetails/annual/PaymentSchedule";
import {lazyInject} from "@athena-portal/core";

interface IPolicyWillBeRenewedConfirmationProps {
    currentPolicy: Policy;
    renewalProposal: Renewal;
    party: Party;
    showHideWillBeRenewedConfirmation: (display: boolean) => void;
    showAutoRenewalQuestionModal: () => void;
    mainCardId: string;
}

interface IState {
    isInstalmentPlanOpened: boolean,
    instalmentsInfo?: InstalmentsInfo
}

// Confirmation banner in footer to inform that policy renewal is turned on and policy will be renewed
export class PolicyWillBeRenewedConfirmation extends React.Component<IPolicyWillBeRenewedConfirmationProps, IState> {

    @lazyInject(Types.CustomerPortalService)
    private readonly customerPortalService: ICustomerPortalService;

    constructor(props: IPolicyWillBeRenewedConfirmationProps) {
        super(props);

        this.state = {
            isInstalmentPlanOpened: false
        };
    }
    public async componentDidMount() {
        const agreementVersionIds: number[] = this.props.currentPolicy.policyHistoryList
            .filter(ph => ph.status !== POLICY_STATUS_MTC)
            .map(ph => ph.id);

        this.customerPortalService.getCombinedInstalmentsInfo(agreementVersionIds)
            .subscribe((instalmentsInfo: any) => {
                this.setState({instalmentsInfo});
            });
    }

    public render() {
        const modalTitle: React.ReactNode = <h1>You’ve renewed!</h1>;
        const renewalStartDate = formatDateDMY(this.props.renewalProposal.headerInfo.startDate);
        const premiumType: string = this.props.renewalProposal.factors.premiumType;

        return (
            <>
                {<StorageSuccessModal
                    title={modalTitle}
                    onClose={this.hideAutoRenewalSuccessModal}
                    explanationWording={
                        <>
                            <p className="font-weight-semi-bold">
                                Your {resourcesHelper.PRODUCT} Car Insurance policy is now scheduled to renew on {renewalStartDate}.
                                You do not need to inform us of the change by email or do anything else.
                            </p>

                            {premiumType === PREMIUM_TYPE_MONTHLY && this.monthlyInfo()}
                            {premiumType === PREMIUM_TYPE_ANNUAL && this.annualInfo()}
                        </>
                    }
                    anythingElseTitle={
                        EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle()}
                    explanationAnythingElseWording={
                        <>
                            Please check everything’s still accurate and make any changes required so you can be
                            sure
                            all policy details are up to date.<br/>
                            You can still <Link
                            className="text-decoration-underline cursor-pointer text-blue-1 muli-bold-16"
                            to={RENEWAL_PROPOSAL_EDIT_VIEW_JOURNEY_URL + this.props.renewalProposal._id}>
                            make changes</Link> up to your renewal date.
                        </>
                    }/>
                }
            </>
        )
    }

    private monthlyInfo(): React.ReactNode {
        const renewalProposal: Renewal = this.props.renewalProposal
        const instalmentPlan: any = renewalProposal.paymentDetails.instalmentPlan;
        const monthlyPayerInitialPaymentDate: string | null = formatDateDMY(instalmentPlan.instalments[0].dueDate);
        const currentPolicyInteraction: any = this.props.currentPolicy.interaction;
        // Fill instalment amounts
        instalmentPlan.instalments.forEach((instalment:Instalment) => {
            instalment.amount = instalmentPlan.monthlyPayment;
        });

        return <>
            <p>The first instalment of <span
                className="font-weight-bold">{getAmountToDisplay(this.props.renewalProposal.paymentDetails.instalmentPlan.monthlyPayment)}</span> will
                be taken on {monthlyPayerInitialPaymentDate}.
            </p>
            <p className="mb-5">
                Please check your payment schedule below and make sure you're happy with your
                current <Link
                className="text-decoration-underline cursor-pointer text-blue-1 muli-bold-16"
                to={`/dashboard/annualPaymentHistory/${this.props.currentPolicy.policyNumber}`}>payment details</Link>.
            </p>
            <label htmlFor="paymentScheduleTable" className="text-center">
                <div className="font-weight-bold">Payment schedule</div>
            </label>
            <p className="mb-2 mt-2">You have to pay a total of <span
                className="font-weight-bold">{getAmountToDisplay(instalmentPlan.totalAmount)} </span>
                ({instalmentPlan.instalments.length} instalments X {getAmountToDisplay(instalmentPlan.monthlyPayment)})</p>
            <div id="paymentScheduleTable" className="mb-3 mt-3">
                <PaymentSchedule
                    isOpen={this.state.isInstalmentPlanOpened}
                    instalments={instalmentPlan.instalments || []}
                    isPaidOff={false}
                    interaction={currentPolicyInteraction}
                />
            </div>
            <div className="mt-3 mb-5">
                {!this.state.isInstalmentPlanOpened ?
                    <button
                        className="btn-inner-link btn-text text-center cursor-pointer text-decoration-none mb-5"
                        onClick={this.toggleInstallmentPlan}>
                        + <span className="text-decoration-underline">View instalments plan</span>
                    </button>
                    :
                    <button
                        className="btn-inner-link btn-text text-center cursor-pointer text-decoration-none mb-5"
                        onClick={this.toggleInstallmentPlan}>
                        - <span className="text-decoration-underline">Hide instalments plan</span>
                    </button>
                }
            </div>
        </>;
    }

    private annualInfo(): React.ReactNode {
        const renewalStartDate: string | null = formatDateDMY(this.props.renewalProposal.headerInfo.startDate);
        const paymentCard: PaymentCard | null = findMainAnnualPaymentCardByMainCardId(this.props.party, this.props.mainCardId);
        const cardEnding: string = paymentCard!.cardNumberObfuscated.split('******')[1];

        return <>
            <p>
                <span className="font-weight-bold">{getAmountToDisplay(this.props.renewalProposal.factors.annualPremium)}</span> will be
                taken from your card ending in <span
                className="font-weight-bold">{cardEnding}</span> on {renewalStartDate}.
            </p>
            <p>
                Please make sure you’re happy with your current <Link
                className="text-decoration-underline cursor-pointer text-blue-1 muli-bold-16"
                to={`/dashboard/annualPaymentHistory/${this.props.currentPolicy.policyNumber}`}>payment details</Link> and your card is
                still valid.
            </p>
        </>;
    }

    private readonly toggleInstallmentPlan = (): void => {
        this.setState((prevState: Readonly<IState>) => (
            {isInstalmentPlanOpened: !prevState.isInstalmentPlanOpened})
        )
    }

    private readonly hideAutoRenewalSuccessModal = () => {
        this.props.showHideWillBeRenewedConfirmation(false);
        this.props.showAutoRenewalQuestionModal();
        window.history.pushState({}, document.title, window.location.pathname);
    }
}
